import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";
import { breadcrumbPageLabel, isBreadcrumbLandingPage } from "@/lib/navigationMeta";

type AppHeaderBreadcrumbsProps = {
  className?: string;
};

/** Teal “Home” link — matches COMPLY.NOW shell (see EA Data Management). */
const homeLinkClass =
  "text-[13px] font-medium !text-[hsl(158_74%_38%)] no-underline transition-colors visited:!text-[hsl(158_74%_38%)] hover:!text-[hsl(158_74%_32%)] hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(158_74%_45%/0.45)]";

const listClass =
  "flex flex-wrap items-center gap-x-1 gap-y-0.5 text-[13px] leading-snug text-slate-500 sm:gap-x-1.5";

const currentPageClass =
  "font-normal !text-slate-800 max-w-[min(100vw-12rem,28rem)] truncate";

const sepClass =
  "inline-flex items-center justify-center px-0.5 text-[13px] font-normal text-slate-400 select-none";

export function AppHeaderBreadcrumbs({ className }: AppHeaderBreadcrumbsProps) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const currentLabel = breadcrumbPageLabel(pathname);
  const homeLabel = t("Sidebar.Home");
  const dashboardLabel = t("Sidebar.Dashboard");

  const normalized = pathname.replace(/\/$/, "") || "/";
  const isRoot = normalized === "/";

  if (isBreadcrumbLandingPage(pathname)) {
    const singleLabel = isRoot ? dashboardLabel : currentLabel;
    return (
      <Breadcrumb className={cn("app-shell-breadcrumb min-w-0", className)}>
        <BreadcrumbList className={cn(listClass)}>
          <BreadcrumbItem>
            <BreadcrumbPage className={cn(currentPageClass, "max-w-[min(100vw-10rem,24rem)]")}>
              {singleLabel}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  return (
    <Breadcrumb className={cn("app-shell-breadcrumb min-w-0", className)}>
      <BreadcrumbList className={listClass}>
        <BreadcrumbItem className="inline-flex items-center">
          <BreadcrumbLink asChild>
            <Link to="/" className={homeLinkClass}>
              {homeLabel}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className={sepClass}>&gt;</BreadcrumbSeparator>
        <BreadcrumbItem className="inline-flex items-center">
          <BreadcrumbPage className={currentPageClass}>{currentLabel}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
